<template>
  <div class="surat-masuk-page">
    <div class="page-header">
      <div>
        <h1>Surat Masuk</h1>
      </div>
    </div>

    <div class="content-container">
      <!-- Filters -->
      <div class="filters-section">
        <a-row :gutter="16">
          <a-col :xs="24" :sm="16" :md="20">
            <a-input
              v-model:value="filters.search"
              placeholder="Cari nomor surat, pengirim, atau perihal"
              size="large"
              @pressEnter="handleSearch"
            >
              <template #prefix>
                <SearchOutlined style="color: rgba(0, 0, 0, 0.25); margin-right: 4px" />
              </template>
            </a-input>
          </a-col>
          <a-col :xs="24" :sm="8" :md="4">
            <button
              v-if="userRole !== 'ketua' && userRole !== 'admin'"
              @click="navigateToRegistrasi"
              class="tambah-button"
            >
              <plus-outlined />
              TAMBAH
            </button>
          </a-col>
        </a-row>
      </div>

      <!-- Surat Masuk Table -->
      <div class="surat-table">
        <a-table
          :columns="columns"
          :data-source="suratMasukList"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 1500 }"
          row-key="id"
          bordered
          :custom-row="customRow"
          @change="handleTableChange"
        >
          <template #filterDropdown="{ column }">
            <div v-if="column.key === 'waktuPenerimaan'" style="padding: 8px">
              <a-range-picker
                v-model:value="dateReceivedRange"
                format="DD-MM-YYYY"
                :placeholder="['Tanggal Mulai', 'Tanggal Akhir']"
                style="width: 280px; margin-bottom: 8px; display: block"
                @change="handleDateReceivedFilterChange"
              />
            </div>
            <div v-else-if="column.key === 'tanggalSurat'" style="padding: 8px">
              <a-range-picker
                v-model:value="dateSuratRange"
                format="DD-MM-YYYY"
                :placeholder="['Tanggal Mulai', 'Tanggal Akhir']"
                style="width: 280px; margin-bottom: 8px; display: block"
                @change="handleDateSuratFilterChange"
              />
            </div>
          </template>

          <template #no="{ record }">
            {{ record.nomorUrut }}
          </template>

          <template #dateReceived="{ record }">
            {{ formatDate(record.waktuPenerimaan) }}
          </template>

          <template #dateSubmitted="{ record }">
            {{ formatDate(record.tanggalSurat) }}
          </template>

          <template #dispositionTo="{ record }">
            <span v-if="record.disposisi">{{ getDisposisiLabel(record) }}</span>
            <span v-else style="color: #999">-</span>
          </template>

          <template #dispositionStatus="{ record }">
            <div style="display: flex; gap: 4px">
              <a-tag v-if="record.status === 'Didisposisikan'" color="orange">
                <strong>{{ record.status }}</strong>
              </a-tag>

              <a-tag v-else-if="record.status === 'Selesai Disposisi'" color="green">
                <strong>{{ record.status }}</strong>
              </a-tag>

              <a-tag v-else-if="record.status === 'Diarsipkan'" color="blue">
                <strong>{{ record.status }}</strong>
              </a-tag>
              <a-tag v-else color="default">{{ record.status || '-' }}</a-tag>

              <!-- Comment Status Tag -->
              <a-tag
                v-if="
                  record.status === 'Didisposisikan' &&
                    record.disposisi === 'pengurus' &&
                    record.commentStatus &&
                    record.commentStatus === 'Dijawab'
                "
                :color="getCommentStatusColor(record.commentStatus)"
              >
                <strong>{{ record.commentStatus }}</strong>
              </a-tag>
            </div>
          </template>
        </a-table>
      </div>

      <!-- Empty State - No data at all -->

      <!-- Pagination -->
      <div v-if="!loading && suratMasukList.length > 0" class="pagination-container">
        <a-pagination
          v-model:current="pagination.page"
          v-model:page-size="pagination.pageSize"
          :total="totalData"
          :show-total="total => `Total ${total} baris`"
          :show-size-changer="true"
          @change="handlePageChange"
        />
      </div>
    </div>

    <!-- Detail Modal -->
    <a-modal
      v-model:visible="detailModalVisible"
      title="Detail Surat Masuk"
      width="700px"
      :footer="null"
    >
      <div v-if="selectedSurat" class="detail-content">
        <a-descriptions bordered :column="1">
          <a-descriptions-item label="Waktu Penerimaan">
            {{ formatDate(selectedSurat.waktuPenerimaan) }}
          </a-descriptions-item>
          <a-descriptions-item label="No. Surat">
            {{ selectedSurat.nomorSurat }}
          </a-descriptions-item>
          <a-descriptions-item label="Tanggal Surat">
            {{ formatDate(selectedSurat.tanggalSurat) }}
          </a-descriptions-item>
          <a-descriptions-item label="Pengirim">
            {{ selectedSurat.pengirim }}
          </a-descriptions-item>
          <a-descriptions-item label="Perihal">
            {{ selectedSurat.perihal }}
          </a-descriptions-item>
          <a-descriptions-item label="Keterangan">
            {{ selectedSurat.keterangan || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="Disposisi">
            {{ getDisposisiLabel(selectedSurat) }}
          </a-descriptions-item>
          <a-descriptions-item label="Status Surat">
            <a-tag v-if="selectedSurat.status === 'Disiapkan'" color="blue">
              {{ selectedSurat.status }}
            </a-tag>
            <a-tag v-else-if="selectedSurat.status === 'Didisposisikan'" color="orange">
              {{ selectedSurat.status }}
            </a-tag>
            <a-tag v-else-if="selectedSurat.status === 'Selesai Disposisi'" color="green">
              {{ selectedSurat.status }}
            </a-tag>
            <a-tag v-else-if="selectedSurat.status === 'Diarsipkan'" color="default">
              {{ selectedSurat.status }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, h, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { notification, DatePicker } from 'ant-design-vue'
import { PlusOutlined, SearchOutlined, UpOutlined, DownOutlined } from '@ant-design/icons-vue'
import { suratMasukAPI, userAPI } from '@/services/axios/perki'
import dayjs from 'dayjs'

export default {
  name: 'SuratMasuk',
  components: {
    PlusOutlined,
    SearchOutlined,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const loading = ref(false)
    const suratMasukList = ref([])
    const totalData = ref(0)
    const detailModalVisible = ref(false)
    const selectedSurat = ref(null)
    const disposisiKepadaOptions = ref([])
    const dateReceivedRange = ref(null)
    const dateSuratRange = ref(null)

    const state = reactive({
      filteredInfo: null,
      sortedInfo: null,
    })

    // Get user's group_id and role from localStorage
    const userGroupId = localStorage.getItem('perki_group_id')
    const userRole = ref(localStorage.getItem('perki_role'))

    const filters = reactive({
      search: '',
      dateReceivedFrom: null,
      dateReceivedTo: null,
      dateSuratFrom: null,
      dateSuratTo: null,
      status: [],
      disposisi: [],
    })

    const pagination = reactive({
      page: 1,
      pageSize: 10,
    })

    // Debounce timer for search
    let searchDebounceTimer = null

    // Watch for search changes with debounce
    watch(
      () => filters.search,
      () => {
        if (searchDebounceTimer) {
          clearTimeout(searchDebounceTimer)
        }
        searchDebounceTimer = setTimeout(() => {
          pagination.page = 1
          fetchSuratMasuk()
        }, 500)
      },
    )

    const columns = computed(() => [
      {
        title: 'No. Urut',
        dataIndex: 'no',
        key: 'no',
        width: 100,
        align: 'center',
        slots: { customRender: 'no' },
      },
      {
        title: 'Waktu Diterima',
        dataIndex: 'waktuPenerimaan',
        key: 'waktuPenerimaan',
        width: 170,
        slots: {
          customRender: 'dateReceived',
          filterDropdown: 'filterDropdown',
        },
        sorter: true,
        sortOrder:
          state.sortedInfo?.field === 'waktuPenerimaan' && state.sortedInfo?.order
            ? state.sortedInfo.order
            : null,
        filteredValue: dateReceivedRange.value ? [dateReceivedRange.value] : null,
      },
      {
        title: 'No. Surat',
        dataIndex: 'nomorSurat',
        key: 'nomorSurat',
        width: 180,
      },
      {
        title: 'Tanggal Surat',
        dataIndex: 'tanggalSurat',
        key: 'tanggalSurat',
        width: 150,
        slots: {
          customRender: 'dateSubmitted',
          filterDropdown: 'filterDropdown',
        },
        sorter: true,
        sortOrder:
          state.sortedInfo?.field === 'tanggalSurat' && state.sortedInfo?.order
            ? state.sortedInfo.order
            : null,
        filteredValue: dateSuratRange.value ? [dateSuratRange.value] : null,
      },
      {
        title: 'Pengirim',
        dataIndex: 'pengirim',
        key: 'pengirim',
        width: 150,
      },
      {
        title: 'Disposisi Kepada',
        dataIndex: 'disposisi',
        key: 'disposisi',
        width: 180,
        slots: { customRender: 'dispositionTo' },
        filters: disposisiKepadaOptions.value,
        filteredValue: state.filteredInfo?.disposisi || null,
        // onFilter: (value, record) => record.disposisi === value,
      },
      {
        title: 'Perihal',
        dataIndex: 'perihal',
        key: 'perihal',
        width: 250,
      },
      {
        title: 'Status Surat',
        dataIndex: 'status',
        key: 'status',
        width: 200,
        slots: { customRender: 'dispositionStatus' },
        filters: [
          { text: 'Didisposisikan', value: 'Didisposisikan' },
          { text: 'Dijawab', value: 'Dijawab' },
          { text: 'Selesai Disposisi', value: 'Selesai Disposisi' },
          { text: 'Diarsipkan', value: 'Diarsipkan' },
        ],
        filteredValue: state.filteredInfo?.status || null,
        onFilter: (value, record) => record.status === value || record.commentStatus === value,
      },
    ])

    const formatDate = dateString => {
      if (!dateString) return '-'
      return dayjs(dateString).format('DD MMMM YYYY')
    }

    const getDisposisiLabel = surat => {
      if (!surat.disposisi) return '-'
      if (surat.disposisi === 'tidak didisposisi') return '-'

      if (surat.disposisi === 'pekerja harian') {
        if (!surat.disposisiPekerjaHarian || surat.disposisiPekerjaHarian.length === 0) return '-'

        // Collect all unique users from all disposisi pekerja harian
        const allUsers = []
        surat.disposisiPekerjaHarian.forEach(disposisi => {
          if (disposisi.disposisiKepadaUser && disposisi.disposisiKepadaUser.length > 0) {
            disposisi.disposisiKepadaUser.forEach(user => {
              if (user.name && !allUsers.includes(user.name)) {
                allUsers.push(user.name)
              }
            })
          }
        })

        return allUsers.length > 0 ? allUsers.join(', ') : '-'
      }

      if (surat.disposisi === 'pengurus') {
        if (!surat.disposisiPengurus || surat.disposisiPengurus.length === 0) return '-'
        const allUsers = []
        surat.disposisiPengurus.forEach(disposisi => {
          if (disposisi.disposisiKepadaUser && disposisi.disposisiKepadaUser.length > 0) {
            disposisi.disposisiKepadaUser.forEach(user => {
              if (user.name && !allUsers.includes(user.name)) {
                allUsers.push(user.name)
              }
            })
          }
        })

        return allUsers.length > 0 ? allUsers.join(', ') : '-'
      }

      if (surat.disposisi === 'penugasan dinas') {
        if (!surat.suratKeluar?.perwakilanDinas || surat.suratKeluar.perwakilanDinas.length === 0)
          return '-'
        return surat.suratKeluar.perwakilanDinas
          .map(item => item.user?.name || item.namaNonPengurus || '-')
          .join(', ')
      }

      return '-'
    }

    const getCommentStatusColor = status => {
      switch (status) {
        case 'Dijawab':
          return 'pink'
        case 'Menunggu':
          return 'blue'
        case 'Baru':
          return 'red'
        default:
          return 'default'
      }
    }

    const fetchSuratMasuk = async () => {
      if (!userGroupId) {
        notification.error({ message: 'Group ID tidak ditemukan. Silakan login kembali.' })
        return
      }

      loading.value = true
      try {
        // Determine sortBy and sortOrder
        let sortBy = 'createdAt'
        let sortOrder = 'DESC'
        if (state.sortedInfo && state.sortedInfo.field) {
          sortBy = state.sortedInfo.field
        }
        if (state.sortedInfo && state.sortedInfo.order) {
          sortOrder = state.sortedInfo.order === 'ascend' ? 'ASC' : 'DESC'
        }

        const params = {
          page: pagination.page,
          pageSize: pagination.pageSize,
          sortBy: sortBy,
          sortOrder: sortOrder,
        }

        // Add search filter
        if (filters.search) {
          params.q = filters.search
        }

        // Add status filter
        if (filters.status && filters.status.length > 0) {
          params.status = filters.status.join(',')
        }

        // Add disposisi filter
        if (filters.disposisi && filters.disposisi.length > 0) {
          params.disposisi = filters.disposisi.join(',')
        }

        // Add date received filter
        if (filters.dateReceivedFrom) {
          params.dateReceivedFrom = filters.dateReceivedFrom
        }
        if (filters.dateReceivedTo) {
          params.dateReceivedTo = filters.dateReceivedTo
        }

        // Add date surat filter
        if (filters.dateSuratFrom) {
          params.dateSuratFrom = filters.dateSuratFrom
        }
        if (filters.dateSuratTo) {
          params.dateSuratTo = filters.dateSuratTo
        }

        const response = await suratMasukAPI.getAll(params)
        const data = response.data?.data || {}

        // Map the response to flatten the structure and include commentStatus
        suratMasukList.value = (data.suratMasuk || []).map(item => ({
          ...item.data,
          commentStatus: item.commentStatus,
          nomorUrut: item.nomorUrut,
        }))
        totalData.value = data.pagination?.totalRecords || 0
      } catch (error) {
        console.error('Error fetching surat masuk:', error)
        notification.error({ message: 'Gagal memuat data surat masuk' })
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      pagination.page = 1
      fetchSuratMasuk()
    }

    const handleDateReceivedFilterChange = () => {
      pagination.page = 1
      if (dateReceivedRange.value && dateReceivedRange.value.length === 2) {
        filters.dateReceivedFrom = dateReceivedRange.value[0].format('DD-MM-YYYY')
        filters.dateReceivedTo = dateReceivedRange.value[1].format('DD-MM-YYYY')
      } else {
        filters.dateReceivedFrom = null
        filters.dateReceivedTo = null
      }
      fetchSuratMasuk()
    }

    const handleDateSuratFilterChange = () => {
      pagination.page = 1
      if (dateSuratRange.value && dateSuratRange.value.length === 2) {
        filters.dateSuratFrom = dateSuratRange.value[0].format('DD-MM-YYYY')
        filters.dateSuratTo = dateSuratRange.value[1].format('DD-MM-YYYY')
      } else {
        filters.dateSuratFrom = null
        filters.dateSuratTo = null
      }
      fetchSuratMasuk()
    }

    const navigateToRegistrasi = () => {
      router.push('/surat-masuk/registrasi')
    }

    const customRow = record => {
      return {
        onClick: () => {
          router.push(`/surat-masuk/update/${record.id}`)
        },
      }
    }

    const handlePageChange = (page, pageSize) => {
      pagination.page = page
      pagination.pageSize = pageSize
      fetchSuratMasuk()
    }

    const handleTableChange = (pag, tableFilters, sorter) => {
      pagination.page = pag.current
      pagination.pageSize = pag.pageSize
      state.filteredInfo = tableFilters
      state.sortedInfo = sorter

      // Handle status filter
      if (tableFilters.status && tableFilters.status.length > 0) {
        filters.status = tableFilters.status
      } else {
        filters.status = []
      }

      // Handle disposisi filter
      if (tableFilters.disposisi && tableFilters.disposisi.length > 0) {
        filters.disposisi = tableFilters.disposisi
      } else {
        filters.disposisi = []
      }

      // Refetch with new filters and sorting
      fetchSuratMasuk()
    }

    const fetchGroupUsers = async () => {
      try {
        const response = await userAPI.getUsersInMyGroup()
        if (response.data?.data?.users) {
          const users = response.data.data.users

          disposisiKepadaOptions.value = users
            .filter(user => user.role !== 'admin')
            .map(user => ({
              text: user.name,
              value: user.name,
            }))

          disposisiKepadaOptions.value.push({
            text: 'Non Pengurus',
            value: 'Non Pengurus',
          })
        }
      } catch (error) {
        console.error('Error fetching group users:', error)
        notification.error({ message: 'Error', description: 'Gagal memuat daftar pengguna' })
      }
    }

    const showDetailModal = record => {
      selectedSurat.value = record
      detailModalVisible.value = true
    }

    const applyStatusFilter = () => {
      const statusParam = route.query.status
      if (statusParam) {
        const statuses = Array.isArray(statusParam)
          ? statusParam
          : String(statusParam)
              .split(',')
              .map(s => s.trim())
        filters.status = statuses
        state.filteredInfo = { status: statuses }
        pagination.page = 1
      }
    }

    onMounted(async () => {
      if (userGroupId) {
        await fetchGroupUsers()
      }
      applyStatusFilter()
      await fetchSuratMasuk()
    })

    return {
      loading,
      suratMasukList,
      totalData,
      detailModalVisible,
      selectedSurat,
      filters,
      pagination,
      dateReceivedRange,
      dateSuratRange,
      state,
      columns,
      userRole,
      formatDate,
      getDisposisiLabel,
      getCommentStatusColor,
      fetchSuratMasuk,
      handleSearch,
      handleDateReceivedFilterChange,
      handleDateSuratFilterChange,
      navigateToRegistrasi,
      customRow,
      handlePageChange,
      handleTableChange,
      showDetailModal,
    }
  },
}
</script>

<style lang="scss" scoped>
.surat-masuk-page {
  min-height: calc(100vh - 64px);
  background: #fcfcfc;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding: 24px 24px 0 24px;
    border-radius: 8px;

    @media (max-width: 768px) {
      padding: 0;
    }

    h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #282828;
    }

    p {
      margin: 4px 0 0;
      color: #8c8c8c;
      font-size: 14px;
    }
  }

  .content-container {
    padding: 0 24px 24px 24px;
    border-radius: 8px;

    @media (max-width: 768px) {
      padding: 0;
    }

    .filters-section {
      margin-bottom: 24px;

      :deep(.ant-input::placeholder) {
        color: #999999 !important;
        opacity: 1 !important;
        font-size: 14px;
      }

      :deep(.ant-btn-primary) {
        &:hover {
          background: white !important;
          border-color: #e10f28 !important;
          color: #e10f28 !important;
        }
      }
    }

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 400px;
    }

    .surat-table {
      margin-bottom: 16px;

      :deep(.ant-table) {
        font-size: 14px;
      }

      :deep(.ant-table-thead > tr > th) {
        background: #fafafa;
        font-weight: 600;
        color: #262626;
      }

      :deep(.ant-table-tbody > tr) {
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
          background-color: #f5f5f5;
        }
      }
    }

    .empty-state {
      display: flex;
      padding-top: 80px;
      justify-content: center;
      min-height: 400px;
    }

    .pagination-container {
      display: flex;
      justify-content: flex-end;
      margin-top: 16px;
    }
  }

  .detail-content {
    :deep(.ant-descriptions-item-label) {
      font-weight: 600;
      background: #fafafa;
    }
  }
}

.tambah-button {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  width: 100%;
  background: #e10f28;
  color: white;
  padding: 6px 12px;
  transition: all 0.3s;
  font-family: 'Barlow' !important;

  @media (max-width: 576px) {
    margin-top: 12px;
  }

  &:hover {
    background: white !important;
    border-color: #e10f28 !important;
    color: #e10f28 !important;
  }
}

::v-deep(.ant-tag) {
  font-weight: 700;
  border-radius: 50px;
}

::v-deep(.ant-table-thead > tr > th) {
  background: #f2f4f8 !important;
}

::v-deep(.ant-table) {
  border-collapse: collapse;
}

::v-deep(.ant-table-bordered .ant-table-body > table) {
  border: none !important;
}

::v-deep(.ant-table-thead > tr > th) {
  border-right: none !important;
}

::v-deep(.ant-table-tbody > tr > td) {
  border-right: none !important;
  background: white !important;
}

::v-deep(.ant-table-tbody
    > tr:hover:not(.ant-table-expanded-row):not(.ant-table-row-selected)
    > td) {
  background: #e4e9f0 !important;
}

::v-deep(.ant-table-tbody > tr > td.ant-table-column-sort) {
  background: white !important;
}
</style>
